'use client'

import SectionHeader from '@/components/ui/SectionHeader'
import LinkWithIcon from '@/components/ui/LinkWithIcon'
import { Media } from '@/components/Media'
import { cn } from '@/utilities/ui'
import type { CardsWithLogosBlock } from '@/payload-types'

type CardItem = CardsWithLogosBlock['cards'][number]

interface CardsWithLogosClientProps {
  header: {
    infoTagText?: string | null
    title: string
    subtitle?: string | null
  }
  cards: CardItem[]
  isDarkMode?: boolean
  sectionBackground?: 'white' | 'light-gray' | 'dark'
}

export default function CardsWithLogosClient({
  header,
  cards,
  isDarkMode = false,
  sectionBackground = 'white',
}: CardsWithLogosClientProps) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Header column */}
      <div className="flex flex-col">
        <SectionHeader
          infoTagText={header.infoTagText ?? ''}
          title={header.title}
          subtitle={header.subtitle ?? undefined}
          align="left"
          size="sm"
          className="mb-0"
          titleClassName={isDarkMode ? 'on-dark' : 'text-primary'}
          subtitleClassName={isDarkMode ? 'on-dark-secondary' : undefined}
        />
      </div>

      {/* Cards column */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {cards.map((card, index) => {
          const lightLogo = typeof card.logo === 'object' ? card.logo : null
          const darkLogo = typeof card.darkModeLogo === 'object' ? card.darkModeLogo : null
          const hasDark = Boolean(darkLogo)
          const isEven = index % 2 === 0
          const bgClass = isDarkMode
            ? isEven
              ? 'bg-white/5'
              : 'bg-white/10'
            : sectionBackground === 'light-gray'
              ? 'bg-brand-surface-1'
              : isEven
                ? 'bg-light-gray'
                : 'bg-brand-surface-1'

          return (
            <div key={index} className={`${bgClass} p-6 flex flex-col items-start gap-8`}>
              {lightLogo && (
                <div className="h-16">
                  <Media
                    resource={lightLogo}
                    alt={lightLogo.alt || `${card.title} Logo`}
                    pictureClassName={cn(hasDark && 'dark:hidden')}
                    imgClassName="h-16 w-auto object-contain"
                    size="200px"
                    disableBlur
                  />
                  {darkLogo && (
                    <Media
                      resource={darkLogo}
                      alt={darkLogo.alt || `${card.title} Logo`}
                      pictureClassName="hidden dark:block"
                      imgClassName="h-16 w-auto object-contain"
                      size="200px"
                      disableBlur
                    />
                  )}
                </div>
              )}
              <div className="flex flex-col flex-1 w-full gap-4">
                <div>
                  <div className={`title-md ${isDarkMode ? 'on-dark' : 'text-primary'}`}>
                    {card.title}
                  </div>
                  <p
                    className={`caption-lg mt-2 ${isDarkMode ? 'on-dark-secondary' : 'text-secondary'}`}
                  >
                    {card.description}
                  </p>
                </div>
                {card.cardLink && card.cardLink.enabled && (
                  <div className="mt-auto pt-2">
                    <LinkWithIcon
                      link={card.cardLink}
                      variant="link"
                      color={isDarkMode ? 'white' : 'brand'}
                      size="md"
                    />
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
